import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Button,
    Paper,
    Checkbox
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import pocusReportConfig from '../config/pocusReportConfig.json';
import './CompactPocusReport.css';

const CompactPocusReport = ({ fileName }) => {
    const [selections, setSelections] = useState({});
    const [conclusionChecks, setConclusionChecks] = useState({});
    const [reportText, setReportText] = useState('');
    const [conclusionText, setConclusionText] = useState('');

    // Initialize selections and checkboxes with empty values
    useEffect(() => {
        const initialSelections = {};
        const initialChecks = {};
        pocusReportConfig.reportSections.forEach(section => {
            section.descriptors.forEach(descriptor => {
                const key = `${section.section}-${descriptor.descriptor}`;
                initialSelections[key] = '';
                initialChecks[key] = false;
            });
        });
        setSelections(initialSelections);
        setConclusionChecks(initialChecks);
    }, []);

    // Update report text whenever selections or checkboxes change
    useEffect(() => {
        generateReportText();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selections, conclusionChecks]);

    const handleSelectionChange = (sectionName, descriptorName, value) => {
        const key = `${sectionName}-${descriptorName}`;
        setSelections(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleCheckboxChange = (sectionName, descriptorName, checked) => {
        const key = `${sectionName}-${descriptorName}`;
        setConclusionChecks(prev => ({
            ...prev,
            [key]: checked
        }));
    };

    const generateReportText = () => {
        let report = [];
        let conclusions = [];

        pocusReportConfig.reportSections.forEach(section => {
            let sectionSentences = [];
            
            section.descriptors.forEach(descriptor => {
                const key = `${section.section}-${descriptor.descriptor}`;
                const selectedValue = selections[key];
                const includeConclusion = conclusionChecks[key];
                
                if (selectedValue) {
                    const selectedItem = descriptor.items.find(item => item.item === selectedValue);
                    if (selectedItem) {
                        sectionSentences.push(selectedItem.sentence);
                        if (selectedItem.conclusion && includeConclusion) {
                            conclusions.push(selectedItem.conclusion);
                        }
                    }
                }
            });

            if (sectionSentences.length > 0) {
                report.push(`${section.section}: ${sectionSentences.join('. ')}.`);
            }
        });

        setReportText(report.join('\n\n'));
        setConclusionText(conclusions.length > 0 ? `CONCLUSION: ${conclusions.join('. ')}.` : '');
    };

    const clearReport = () => {
        const clearedSelections = {};
        const clearedChecks = {};
        Object.keys(selections).forEach(key => {
            clearedSelections[key] = '';
            clearedChecks[key] = false;
        });
        setSelections(clearedSelections);
        setConclusionChecks(clearedChecks);
        setReportText('');
        setConclusionText('');
    };

    const copyToClipboard = () => {
        const fullReport = `POCUS Report - ${fileName}\n\n${reportText}${conclusionText ? '\n\n' + conclusionText : ''}`;
        navigator.clipboard.writeText(fullReport).then(() => {
            // Could add a snackbar notification here
            console.log('Report copied to clipboard');
        });
    };

    return (
        <Box className="compact-pocus-report-container">
            <Box className="report-header">
                <Typography variant="subtitle2" color="text.secondary">
                    {fileName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                        size="small"
                        variant="outlined" 
                        onClick={clearReport}
                        startIcon={<ClearIcon />}
                    >
                        Clear
                    </Button>
                    <Button 
                        size="small"
                        variant="contained" 
                        onClick={copyToClipboard}
                        disabled={!reportText}
                        startIcon={<ContentCopyIcon />}
                    >
                        Copy
                    </Button>
                </Box>
            </Box>

            <Box className="report-sections">
                {pocusReportConfig.reportSections.map((section, sectionIndex) => (
                    <Paper key={sectionIndex} elevation={1} sx={{ p: 2, mb: 2, width: '100%' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            {section.section}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {section.descriptors.map((descriptor, descriptorIndex) => {
                                const key = `${section.section}-${descriptor.descriptor}`;
                                return (
                                    <Box key={descriptorIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControl size="small" sx={{ flex: 1 }}>
                                            <InputLabel sx={{ fontSize: '0.875rem' }}>{descriptor.descriptor}</InputLabel>
                                            <Select
                                                value={selections[key] || ''}
                                                label={descriptor.descriptor}
                                                onChange={(e) => handleSelectionChange(
                                                    section.section, 
                                                    descriptor.descriptor, 
                                                    e.target.value
                                                )}
                                                sx={{ fontSize: '0.875rem' }}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {descriptor.items.map((item, itemIndex) => (
                                                    <MenuItem key={itemIndex} value={item.item}>
                                                        {item.item}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <Checkbox
                                            size="small"
                                            checked={conclusionChecks[key] || false}
                                            onChange={(e) => handleCheckboxChange(
                                                section.section,
                                                descriptor.descriptor,
                                                e.target.checked
                                            )}
                                            disabled={!selections[key]}
                                            title="Include in conclusion"
                                            sx={{ padding: 0.5 }}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                ))}
            </Box>

            <Box className="report-output">
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Generated Report
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    rows={4}
                    value={reportText}
                    onChange={(e) => setReportText(e.target.value)}
                    placeholder="Select options above to generate report..."
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: '0.875rem' }}
                />
                
                {conclusionText && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Conclusion
                        </Typography>
                        <TextField
                            multiline
                            fullWidth
                            rows={2}
                            value={conclusionText}
                            onChange={(e) => setConclusionText(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: '0.875rem' }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CompactPocusReport;